import React, { useState, useEffect } from 'react';
import {
  getActiveImports,
  getPendingImports,
  getCompletedImports,
  startImport,
  FeedImportStatus,
} from '../services/api';
import './ImportsPage.css';

const ImportsPage: React.FC = () => {
  const [activeImports, setActiveImports] = useState<FeedImportStatus[]>([]);
  const [pendingImports, setPendingImports] = useState<FeedImportStatus[]>([]);
  const [completedImports, setCompletedImports] = useState<FeedImportStatus[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFeedType, setSelectedFeedType] = useState('HOURLY');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadImports();
    const interval = setInterval(loadImports, 5000); // Aggiorna ogni 5 secondi
    return () => clearInterval(interval);
  }, []);

  const loadImports = async () => {
    try {
      const [active, pending, completed] = await Promise.all([
        getActiveImports(),
        getPendingImports(),
        getCompletedImports(),
      ]);
      setActiveImports(active);
      setPendingImports(pending);
      setCompletedImports(completed.slice(0, 10)); // Mostra solo le ultime 10
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Errore nel caricamento delle importazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleStartImport = async () => {
    setImporting(true);
    try {
      await startImport(selectedFeedType);
      await loadImports();
      alert(`Importazione ${selectedFeedType} avviata con successo!`);
    } catch (err: any) {
      alert(`Errore: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      pending: 'badge-warning',
      downloading: 'badge-info',
      processing: 'badge-info',
      completed: 'badge-success',
      failed: 'badge-danger',
    };
    return badges[status] || 'badge-info';
  };

  const getStatusIcon = (status: string) => {
    const icons: Record<string, string> = {
      pending: '⏳',
      downloading: '⬇️',
      processing: '⚙️',
      completed: '✅',
      failed: '❌',
    };
    return icons[status] || '❓';
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('it-IT');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : Date.now();
    const duration = Math.floor((endTime - startTime) / 1000);

    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const renderImportCard = (importItem: FeedImportStatus) => (
    <div key={importItem.id} className="import-card card">
      <div className="import-header">
        <div className="import-title">
          <span className="import-icon">
            {getStatusIcon(importItem.status)}
          </span>
          <div>
            <h4>{importItem.feedType}</h4>
            <p className="import-category">{importItem.category}</p>
          </div>
        </div>
        <span className={`badge ${getStatusBadge(importItem.status)}`}>
          {importItem.status.toUpperCase()}
        </span>
      </div>

      <div className="import-progress">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${importItem.progress}%` }}
          ></div>
        </div>
        <span className="progress-text">{importItem.progress.toFixed(1)}%</span>
      </div>

      <div className="import-details">
        <div className="detail-row">
          <span>Inizio:</span>
          <strong>{formatDate(importItem.startTime)}</strong>
        </div>

        {importItem.endTime && (
          <div className="detail-row">
            <span>Fine:</span>
            <strong>{formatDate(importItem.endTime)}</strong>
          </div>
        )}

        <div className="detail-row">
          <span>Durata:</span>
          <strong>
            {formatDuration(importItem.startTime, importItem.endTime)}
          </strong>
        </div>

        {importItem.fileSize && (
          <div className="detail-row">
            <span>Dimensione:</span>
            <strong>{formatFileSize(importItem.fileSize)}</strong>
          </div>
        )}

        <div className="detail-row">
          <span>Processati:</span>
          <strong>
            {importItem.processedItems.toLocaleString('it-IT')}
            {importItem.totalItems &&
              ` / ${importItem.totalItems.toLocaleString('it-IT')}`}
          </strong>
        </div>

        {importItem.failedItems > 0 && (
          <div className="detail-row">
            <span>Falliti:</span>
            <strong className="text-danger">
              {importItem.failedItems.toLocaleString('it-IT')}
            </strong>
          </div>
        )}

        {importItem.error && (
          <div className="import-error">
            <strong>Errore:</strong> {importItem.error}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="imports-page">
      <div className="container">
        <h2>📥 Gestione Importazioni</h2>

        <div className="import-control card">
          <h3>Avvia Nuova Importazione</h3>
          <div className="control-form">
            <div className="form-group">
              <label>Tipo di Feed</label>
              <select
                className="input"
                value={selectedFeedType}
                onChange={(e) => setSelectedFeedType(e.target.value)}
              >
                <option value="HOURLY">Hourly (Orario)</option>
                <option value="DAILY">Daily (Giornaliero)</option>
                <option value="BOOTSTRAP">Bootstrap (Completo)</option>
              </select>
            </div>

            <button
              className="btn btn-primary"
              onClick={handleStartImport}
              disabled={importing}
            >
              {importing ? 'Avvio in corso...' : '▶️ Avvia Importazione'}
            </button>
          </div>
          <p className="help-text">
            <strong>Bootstrap:</strong> Importazione completa del catalogo (può
            richiedere ore)
            <br />
            <strong>Daily:</strong> Aggiornamenti giornalieri
            <br />
            <strong>Hourly:</strong> Aggiornamenti orari
          </p>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="imports-section">
          <h3>🔄 Importazioni Attive ({activeImports.length})</h3>
          {activeImports.length === 0 ? (
            <div className="empty-state card">
              <p>Nessuna importazione attiva al momento</p>
            </div>
          ) : (
            <div className="imports-grid">
              {activeImports.map(renderImportCard)}
            </div>
          )}
        </div>

        <div className="imports-section">
          <h3>⏳ Importazioni in Coda ({pendingImports.length})</h3>
          {pendingImports.length === 0 ? (
            <div className="empty-state card">
              <p>Nessuna importazione in coda</p>
            </div>
          ) : (
            <div className="imports-grid">
              {pendingImports.map(renderImportCard)}
            </div>
          )}
        </div>

        <div className="imports-section">
          <h3>✅ Importazioni Completate (ultime 10)</h3>
          {completedImports.length === 0 ? (
            <div className="empty-state card">
              <p>Nessuna importazione completata</p>
            </div>
          ) : (
            <div className="imports-grid">
              {completedImports.map(renderImportCard)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportsPage;
